import { AiResult, StructuredSummaryResult } from '../../../../../../../models/AiResult'
import { AiFeature } from '../../../../../../../models/AiFeature'
import { getEmptyResultCopyContent } from '../../../copy'
import { HTML_TYPE, TEXT_TYPE } from '../../../../../../../common/copy'

const _INITIAL_REFERRAL_COUNT_PROPS = { referralAskedCount: 0, referralCount: 0, referringClientCount: 0 }

interface ReferralCountProps {
  referralAskedCount: number
  referralCount: number
  referringClientCount: number
}

export interface AdvisorReferralRefereeResult {
  name: string
  email: string
  phoneNumber: string
  pastReferral: boolean
  futureReferral: boolean
}

export interface AdvisorReferralClientResult {
  name: string
  referralAsked: boolean
  referees: AdvisorReferralRefereeResult[]
  context: Record<string, unknown>[]
  clientId?: string
}

export interface AdvisorReferralResult {
  name: string
  clients: AdvisorReferralClientResult[]
  advisorId?: string
}

export const mapAdvisorReferralResult = (aiResult: AiResult) => {
  const structuredSummaryResult = aiResult.result as StructuredSummaryResult

  if (!Array.isArray(structuredSummaryResult.result)) {
    return null
  }

  return structuredSummaryResult.result.reduce<AdvisorReferralResult[]>((acc, result) => {
    if (typeof result !== 'object' || Array.isArray(result)) {
      return acc
    }

    const { name, clients } = result

    if (!Array.isArray(clients)) {
      return acc
    }

    const mappedClientResults = clients.reduce<AdvisorReferralClientResult[]>((acc, clientResult) => {
      if (typeof clientResult !== 'object' || Array.isArray(clientResult)) {
        return acc
      }

      const { name, referralAsked, referees, context } = clientResult

      if (!Array.isArray(referees)) {
        return acc
      }

      const mappedRefereeResults = referees.reduce<AdvisorReferralRefereeResult[]>((acc, refereeResult) => {
        if (typeof refereeResult !== 'object' || Array.isArray(refereeResult)) {
          return acc
        }

        const { name, email, phoneNumber, pastReferral, futureReferral } = refereeResult

        return [
          ...acc,
          {
            name,
            email,
            phoneNumber,
            pastReferral,
            futureReferral,
          } as AdvisorReferralRefereeResult,
        ]
      }, [])

      return [
        ...acc,
        {
          name,
          referralAsked,
          referees: mappedRefereeResults,
          context,
        } as AdvisorReferralClientResult,
      ]
    }, [])

    return [
      ...acc,
      {
        name,
        clients: mappedClientResults,
      } as AdvisorReferralResult,
    ]
  }, [])
}

export const getReferralCountProps = (clients: AdvisorReferralClientResult[]) =>
  clients.reduce<ReferralCountProps>((acc, client) => {
    const { referees } = client
    const clientReferralCount = referees.reduce((acc, referral) => {
      return referral.pastReferral || referral.futureReferral ? acc + 1 : acc
    }, 0)
    return {
      referralAskedCount: client.referralAsked ? acc.referralAskedCount + 1 : acc.referralAskedCount,
      referralCount: acc.referralCount + clientReferralCount,
      referringClientCount: clientReferralCount > 0 ? acc.referringClientCount + 1 : acc.referringClientCount,
    }
  }, _INITIAL_REFERRAL_COUNT_PROPS)

export const copyAdvisorReferral = (aiFeature: AiFeature, aiResult: AiResult) => {
  const advisorReferralResult = mapAdvisorReferralResult(aiResult)

  if (!advisorReferralResult) {
    return getEmptyResultCopyContent()
  }

  const copiedResults = advisorReferralResult.map((advisorItem) => {
    const { name, clients } = advisorItem

    const { referralAskedCount, referralCount, referringClientCount } = getReferralCountProps(clients)

    const advisorItemInfo =
      !referralAskedCount && !referralCount
        ? `${name} has not asked for or received any referrals.`
        : !referralCount
          ? `${name} has asked for referrals, but has not received any.`
          : `${name} received ${referralCount} referrals from ${referringClientCount} clients:`

    const clientsCopyContent = clients.map((clientItem) => {
      const { name, referees } = clientItem

      const refereesCopyContent = referees.map((refereeItem) => {
        const { name, email, phoneNumber, pastReferral, futureReferral } = refereeItem

        const refereeItemPast = pastReferral ? `- Already referred to ${name} (email: ${email}, phone number: ${phoneNumber})` : ``
        const refereeItemFuture = futureReferral ? `- Will refer to ${name} (email: ${email}, phone number: ${phoneNumber})` : ``

        if (pastReferral && futureReferral) {
          return {
            [TEXT_TYPE]: `${refereeItemPast}\n${refereeItemFuture}`,
            [HTML_TYPE]: `<span>${refereeItemPast}</span><br/><span>${refereeItemFuture}</span>`,
          }
        }

        if (pastReferral) {
          return {
            [TEXT_TYPE]: refereeItemPast,
            [HTML_TYPE]: `<span>${refereeItemPast}</span>`,
          }
        }

        if (futureReferral) {
          return {
            [TEXT_TYPE]: refereeItemFuture,
            [HTML_TYPE]: `<span>${refereeItemFuture}</span>`,
          }
        }

        return {
          [TEXT_TYPE]: '',
          [HTML_TYPE]: '',
        }
      })

      const refereesTextCopyContent = refereesCopyContent.map((e) => e[TEXT_TYPE])
      const refereesHtmlCopyContent = refereesCopyContent.map((e) => e[HTML_TYPE])

      return {
        [TEXT_TYPE]: referees.length ? `\n${name}\n\n${refereesTextCopyContent.join('\n')}` : ``,
        [HTML_TYPE]: referees.length ? `</br><span>${name}</span><br/><br/>${refereesHtmlCopyContent.join('</br>')}` : ``,
      }
    })

    const clientsTextCopyContent = clientsCopyContent.map((e) => e[TEXT_TYPE])
    const clientsHtmlCopyContent = clientsCopyContent.map((e) => e[HTML_TYPE])

    return {
      [TEXT_TYPE]: `${name}\n\n${advisorItemInfo}\n${clientsTextCopyContent.join('\n')}`,
      [HTML_TYPE]: `<b>${name}</b></br></br><span>${advisorItemInfo}</span></br>${clientsHtmlCopyContent.join('</br>')}`,
    }
  })

  return {
    [TEXT_TYPE]: copiedResults.map((c) => c[TEXT_TYPE]).join('\n'),
    [HTML_TYPE]: copiedResults.map((c) => c[HTML_TYPE]).join('<br/>'),
  }
}
